import { apiError } from '../apiError';
import api from '../axios.config';
import { ApiResponse } from 'types';
import { responseFrom } from '../apiEnvelope';

/**
 * Outbound voice campaigns, executed by the voicex runtime.
 *
 * cpass is the control surface only — the runtime owns the dialler, its retry
 * history, throttling and business-hours logic. So statistics come from the
 * runtime rather than being recomputed here, and a campaign's real state is
 * whatever the runtime says it is.
 */
const BASE = '/v1/voice-campaigns';
// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

/** As the runtime returns it. Ids and dates are the runtime's own. */
export interface VoiceCampaign {
  id?: string;
  campaignName?: string;
  description?: string;
  campaignType?: string;
  /** DRAFT | ACTIVE | PAUSED | COMPLETED | ARCHIVED */
  status?: string;
  businessId?: string;
  primaryAgentId?: string;
  defaultCallerId?: string;
  maxConcurrentCalls?: number;
  callsPerMinute?: number;
  startDate?: string;
  endDate?: string;
  maxCallDelay?: number;
  /**
   * Whether the same number may be queued on this campaign more than once.
   * A campaign property rather than a per-upload choice: retry rounds and
   * follow-ups need it every time somebody tops up the list, and the person
   * uploading is often not the person who set the policy. Enforced by the
   * backend, so this is a reflection of the rule and not the rule itself.
   */
  allowDuplicateRecipients?: boolean;
  createdAt?: string;
  updatedAt?: string;
  totalScheduledCalls?: number;
  completedCalls?: number;
  failedCalls?: number;
  inProgressCalls?: number;
  pendingCalls?: number;
  /**
   * Null means NOT MEASURED, not zero. The engine returns null when nothing has
   * been dialled — rendering 0% there claims every call failed.
   */
  successRate?: number | null;
  /** Per-status counts, exactly as the engine grouped them. See CampaignStatistics. */
  callsByStatus?: Record<string, number>;
  /**
   * Exactly which fields may be edited in the campaign's CURRENT status.
   *
   * The server owns that rule and sends it on the DTO, so the edit form is built
   * from this list rather than from a status table copied into the frontend —
   * a copy drifts, and a drifted copy offers an edit the server then refuses.
   * Absent or empty means no edit control is shown at all.
   */
  editableFields?: string[];
  ringTimeoutSeconds?: number;
  maxAttempts?: number;
  retryStrategy?: string;
  retryDelayMinutes?: number;
  respectBusinessHours?: boolean;
  respectDnd?: boolean;
  audioFileUrl?: string;
  ttsMessage?: string;
  ivrFlowId?: number;
  aiAgentId?: number;
  callerId?: string;
  transferNumber?: string;
  contactListId?: number;
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  timezone?: string;
}

/**
 * A partial update to an existing campaign — only the fields that changed, and
 * only those the DTO's `editableFields` offered.
 *
 * camelCase, unlike CreateVoiceCampaign's snake_case: this is the campaign DTO's
 * own shape, which is what the update endpoint takes.
 */
export interface UpdateVoiceCampaign {
  campaignName?: string;
  description?: string;
  callsPerMinute?: number;
  maxConcurrentCalls?: number;
  ringTimeoutSeconds?: number;
  maxAttempts?: number;
  retryStrategy?: string;
  retryDelayMinutes?: number;
  respectBusinessHours?: boolean;
  respectDnd?: boolean;
  // Accepted only while the campaign has not started (DRAFT / SCHEDULED). The
  // server decides that, and says so through editableFields.
  audioFileUrl?: string;
  ttsMessage?: string;
  ivrFlowId?: number;
  aiAgentId?: number;
  callerId?: string;
  transferNumber?: string;
  contactListId?: number;
  campaignType?: string;
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  timezone?: string;
}

/**
 * What the answering engine counts, from GET /{id}/statistics.
 *
 * `callsByStatus` is the part that matters and the reason the flat counters are
 * not enough: NO_ANSWER and ABANDONED are neither completed nor failed, and the
 * engine deliberately keeps them out of `failedCalls` — somebody who was never
 * rung is not a failed call.
 */
export interface CampaignStatistics {
  campaignId?: string;
  totalScheduledCalls?: number;
  completedCalls?: number;
  failedCalls?: number;
  /**
   * Dialled, nobody picked up — rang out, busy or rejected.
   *
   * Optional because only the cpass dialler distinguishes it. The voicex
   * runtime's schedule statuses have no no-answer among them: whether a ringing
   * phone was picked up lives per row in retryHistory[].status, which its
   * statistics endpoint does not aggregate. Absent, not zero — a zero here
   * would read as "nobody missed a call".
   */
  noAnswerCalls?: number;
  /** Given up on, or cancelled with the campaign. Never retried. */
  abandonedCalls?: number;
  /** Deliberately not dialled — the contact gate refused it. */
  skippedCalls?: number;
  inProgressCalls?: number;
  pendingCalls?: number;
  successRate?: number | null;
  callsByStatus?: Record<string, number>;
}

/**
 * Which engine dials for this organisation, and what it will quietly ignore.
 *
 * `acceptedButNotEnforced` is read at runtime rather than hardcoded because it
 * is engine-dependent: the cpass dialler stores a rate limit and does not honour
 * it, and the voicex runtime honours things cpass does not.
 */
export interface CampaignEngineInfo {
  /** CPASS | VOICEX */
  engine?: string;
  dialledBy?: string;
  statisticsSource?: string;
  acceptedButNotEnforced?: string[];
}

/**
 * Retry behaviour, as the runtime models it.
 *
 * Every field is optional on purpose: the runtime has sensible defaults
 * (3 attempts, INCREMENTAL, 15min base, x2 backoff, 180min cap, 10% jitter),
 * and sending an unset field would overwrite a good default with a guess.
 */
export interface RetryConfig {
  max_attempts?: number;
  respect_business_hours?: boolean;
  max_retries_per_day?: number;
  retry_strategy?: 'FIXED' | 'INCREMENTAL' | 'EXPONENTIAL' | 'CUSTOM';
  base_retry_delay_minutes?: number;
  backoff_factor?: number;
  max_retry_delay_minutes?: number;
  jitter_factor?: number;
  final_failure_action?: 'LOG' | 'WEBHOOK' | 'TRANSFER';
  failure_webhook_url?: string;
  failure_transfer_number?: string;
  custom_retry_intervals?: { attempt_number: number; delay_minutes: number }[];
}

/** A number allocated to the business, usable as a caller ID. */
export interface BusinessNumber {
  phoneNumber?: string;
  friendlyName?: string;
  numberType?: string;
  countryCode?: string;
}

/** What we send to create one. Snake_case: the cpass controller's contract. */
export interface CreateVoiceCampaign {
  name: string;
  description?: string;
  /** cpass agent id — resolved to the runtime's agentUuid server-side. */
  agent_id?: number | string;
  caller_id?: string;
  calls_per_minute?: number;
  max_concurrent_calls?: number;
  max_call_delay?: number;
  campaign_type?: string;
  start_date?: string;
  end_date?: string;
  welcome_message?: string;
  retry?: RetryConfig;
  /**
   * TRAI call category (PROMOTIONAL | SERVICE). Optional; the server stores it as the campaign's
   * default after creating it and never forwards it to the runtime. Absent = today's behaviour.
   */
  category?: 'PROMOTIONAL' | 'SERVICE';
}

export interface RecipientInput { phone: string; params?: Record<string, any>; }

/** What the runtime accepted — not what we sent. */
export interface AddRecipientsResult { accepted: number; skipped: number; submitted: number; }

/** One queued call, as the runtime stores it. */
export interface CallScheduleRow {
  id?: string;
  callTo?: string;
  /** The caller ID the engine will dial from. */
  callFrom?: string;
  /** QUEUED | SCHEDULED | DIALLING | COMPLETED | NO_ANSWER | FAILED | ABANDONED */
  callStatus?: string;
  campaignId?: string;
  agentId?: string;
  callInitTime?: string;
  scheduleSource?: string;
  /** The row's own counter: how many times it claims to have been dialled. */
  retryCount?: number;
  lastErrorMessage?: string;
  customParam?: Record<string, any>;
  /**
   * How many attempts the engine has a RECORD of.
   *
   * Deliberately not the same number as `retryCount`. The attempt table is
   * newer than the dialler, so a row dialled before it existed carries a
   * counter and no history, and those earlier attempts are genuinely lost.
   * Reporting the smaller number as if it were the whole story would hide
   * calls that really were placed.
   */
  attemptsRecorded?: number;
  /** One entry per recorded dial, oldest first. */
  attemptHistory?: {
    attemptNo?: number;
    startedAt?: string | null;
    /** The campaign row status after this attempt: COMPLETED | NO_ANSWER | FAILED | SKIPPED. */
    outcome?: string | null;
    /** The switch's own word: ANSWER | NOANSWER | BUSY | CONGESTION. */
    dialStatus?: string | null;
    providerCallId?: string | null;
    lastError?: string | null;
    /** Set when a later hangup report revised what this attempt was first recorded as. */
    correctedAt?: string | null;
  }[];
}

export const voiceCampaignsService = {
  list: async (): Promise<ApiResponse<VoiceCampaign[]>> => {
    try { return responseFrom((await api.get(BASE)).data); } catch (e: any) { return err(e, 'Failed to load campaigns'); }
  },
  /** Voice-capable numbers allocated to this org — the caller-ID options. */
  numbers: async (): Promise<ApiResponse<BusinessNumber[]>> => {
    try { return responseFrom((await api.get(`${BASE}/numbers`)).data); } catch (e: any) { return err(e, 'Failed to load numbers'); }
  },
  get: async (id: string): Promise<ApiResponse<any>> => {
    try { return responseFrom((await api.get(`${BASE}/${id}`)).data); } catch (e: any) { return err(e, 'Failed to load campaign'); }
  },
  /** Created DRAFT — nothing dials until start() is called. */
  create: async (body: CreateVoiceCampaign): Promise<ApiResponse<any>> => {
    try { return responseFrom((await api.post(BASE, body)).data); } catch (e: any) { return err(e, 'Failed to create campaign'); }
  },

  /**
   * Queues recipients. call_init_time may be an ISO instant or epoch millis;
   * the backend normalises it, because the runtime only accepts epoch millis.
   * Omitting it means the runtime dials immediately once the campaign is active.
   */
  addRecipients: async (
    id: string,
    recipients: RecipientInput[],
    opts?: { agent_id?: number | string; caller_id?: string; call_init_time?: string | number },
  ): Promise<ApiResponse<AddRecipientsResult>> => {
    try { return responseFrom((await api.post(`${BASE}/${id}/recipients`, { recipients, ...(opts || {}) })).data); }
    catch (e: any) { return err(e, 'Failed to queue recipients'); }
  },

  /** Paged; the payload is the runtime's Spring Page object. */
  listRecipients: async (id: string, page = 0, size = 50): Promise<ApiResponse<any>> => {
    try { return responseFrom((await api.get(`${BASE}/${id}/recipients`, { params: { page, size } })).data); }
    catch (e: any) { return err(e, 'Failed to load recipients'); }
  },

  start: async (id: string): Promise<ApiResponse<any>> => {
    try { return responseFrom((await api.post(`${BASE}/${id}/start`)).data); } catch (e: any) { return err(e, 'Failed to start campaign'); }
  },
  pause: async (id: string): Promise<ApiResponse<any>> => {
    try { return responseFrom((await api.post(`${BASE}/${id}/pause`)).data); } catch (e: any) { return err(e, 'Failed to pause campaign'); }
  },
  resume: async (id: string): Promise<ApiResponse<any>> => {
    try { return responseFrom((await api.post(`${BASE}/${id}/resume`)).data); } catch (e: any) { return err(e, 'Failed to resume campaign'); }
  },
  /**
   * Ends it. COMPLETED on both engines, never a delete — a campaign stopped
   * mid-run still has to show who was never reached.
   */
  stop: async (id: string): Promise<ApiResponse<any>> => {
    try { return responseFrom((await api.post(`${BASE}/${id}/stop`)).data); } catch (e: any) { return err(e, 'Failed to stop campaign'); }
  },
  statistics: async (id: string): Promise<ApiResponse<CampaignStatistics>> => {
    try { return responseFrom((await api.get(`${BASE}/${id}/statistics`)).data); } catch (e: any) { return err(e, 'Failed to load statistics'); }
  },
  /**
   * Change an existing campaign.
   *
   * Sends ONLY what changed, and only fields the campaign's `editableFields`
   * offered. A field the current status will not accept comes back as a 400
   * whose `message` names it; apiError passes that sentence through.
   *
   * ── endpoint note ────────────────────────────────────────────────────────
   * Sent to `/v1/voice-campaigns/{id}`, the resource this screen actually read
   * and the only id it holds. `/obd/campaigns/{id}` is a different, parked
   * engine — its controller says so in its own header — whose ids are not
   * these, so writing one there could only 404.
   */
  update: async (id: string, changes: UpdateVoiceCampaign): Promise<ApiResponse<VoiceCampaign>> => {
    try { return responseFrom((await api.patch(`${BASE}/${id}`, changes)).data); }
    catch (e: any) { return err(e, 'Failed to update campaign'); }
  },

  /** Which engine dials for this organisation, and what it accepts then ignores. */
  engine: async (): Promise<ApiResponse<CampaignEngineInfo>> => {
    try { return responseFrom((await api.get(`${BASE}/engine`)).data); } catch (e: any) { return err(e, 'Failed to read the campaign engine'); }
  },
};
